import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams, useBlocker } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
} from "@/components/ui/select";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import ImageUpload from "@/components/ImageUpload";
import IconPicker from "@/components/IconPicker";
import LivePreview from "@/components/LivePreview";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
    ArrowLeft,
    Plus,
    GripVertical,
    Trash2,
    Star,
    Save,
    Copy,
    Eye,
    Smartphone,
    PencilLine,
    ChevronDown,
    Palette,
    AlertTriangle,
} from "lucide-react";

const emptyBusiness = () => ({
    slug: "",
    is_active: true,
    name: { en: "", ar: "" },
    welcome: { en: "", ar: "" },
    background_url: null,
    hero_title: { en: "", ar: "" },
    hero_subtitle: { en: "", ar: "" },
    footer_text: { en: "", ar: "" },
    background_color: "#0B0B0F",
    text_color: "#FAFAFA",
    card_bg_color: "#FFFFFF",
    card_text_color: "#111111",
    button_bg_color: "#FFFFFF",
    button_text_color: "#111111",
    featured_bg_color: "#F5B301",
    featured_text_color: "#111111",
    overlay_opacity: 0.35,
    buttons: [],
    default_lang: "en",
});

function slugifyClient(s) {
    return (s || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

// Small media query hook so we conditionally mount ONE editor panel per breakpoint
// (avoids DOM duplication + stale state from hidden copies).
function useMediaQuery(query) {
    const [matches, setMatches] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia(query).matches : false
    );
    useEffect(() => {
        const mq = window.matchMedia(query);
        const handler = (e) => setMatches(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, [query]);
    return matches;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Deep clone from a template to a brand-new business (strip identity fields)
function stripTemplate(src) {
    if (!src) return emptyBusiness();
    const clone = JSON.parse(JSON.stringify(src));
    delete clone.id;
    delete clone.created_at;
    delete clone.updated_at;
    clone.slug = "";
    clone.name = { en: "", ar: "" };
    clone.is_active = true;
    // Regenerate button ids
    clone.buttons = (clone.buttons || []).map((b) => ({
        ...b,
        id: crypto.randomUUID(),
    }));
    return { ...emptyBusiness(), ...clone };
}

export default function BusinessEditor() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const templateId = searchParams.get("template");
    const isNew = !id;
    const navigate = useNavigate();

    const [business, setBusiness] = useState(emptyBusiness());
    const [originalSnapshot, setOriginalSnapshot] = useState(JSON.stringify(emptyBusiness()));
    const [loading, setLoading] = useState(!isNew || !!templateId);
    const [saving, setSaving] = useState(false);
    const [slugTouched, setSlugTouched] = useState(false);
    const [mobileTab, setMobileTab] = useState("edit");
    const [allBusinesses, setAllBusinesses] = useState([]);
    const [pickedTemplate, setPickedTemplate] = useState("");
    const isDesktop = useMediaQuery("(min-width: 1024px)");

    // Load either the business (edit) or the template (new)
    useEffect(() => {
        (async () => {
            if (!isNew) {
                try {
                    const { data } = await api.get(`/businesses/${id}`);
                    // Ensure new fields exist
                    const merged = { ...emptyBusiness(), ...data };
                    setBusiness(merged);
                    setOriginalSnapshot(JSON.stringify(merged));
                    setSlugTouched(true);
                } catch (e) {
                    toast.error(formatApiError(e));
                    navigate("/admin", { replace: true });
                }
                setLoading(false);
                return;
            }
            // New business: load list for template selector
            try {
                const { data } = await api.get("/businesses");
                setAllBusinesses(data);
                if (templateId) {
                    const src = data.find((b) => b.id === templateId);
                    if (src) {
                        const seeded = stripTemplate(src);
                        setBusiness(seeded);
                        setOriginalSnapshot(JSON.stringify(seeded));
                        setPickedTemplate(templateId);
                        toast.success(`Loaded template from "${src.name?.en || src.slug}"`);
                    }
                }
            } catch (e) {
                // non-fatal
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isNew, templateId]);

    // Auto-slug from EN name when creating
    useEffect(() => {
        if (isNew && !slugTouched) {
            setBusiness((b) => ({ ...b, slug: slugifyClient(b.name?.en || "") }));
        }
    }, [business.name?.en, isNew, slugTouched]);

    const dirty = useMemo(() => {
        return JSON.stringify(business) !== originalSnapshot;
    }, [business, originalSnapshot]);

    // Warn before closing the tab
    useEffect(() => {
        const handler = (e) => {
            if (!dirty) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    // Warn on in-app navigation with unsaved changes
    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        dirty && currentLocation.pathname !== nextLocation.pathname
    );

    const update = (patch) => setBusiness((b) => ({ ...b, ...patch }));
    const updateName = (lang, v) => setBusiness((b) => ({ ...b, name: { ...b.name, [lang]: v } }));
    const updateWelcome = (lang, v) => setBusiness((b) => ({ ...b, welcome: { ...b.welcome, [lang]: v } }));
    const updateLocalized = (field, lang, v) =>
        setBusiness((b) => ({ ...b, [field]: { ...(b[field] || { en: "", ar: "" }), [lang]: v } }));

    const addButton = () => {
        setBusiness((b) => ({
            ...b,
            buttons: [
                ...(b.buttons || []),
                {
                    id: crypto.randomUUID(),
                    icon: "link",
                    icon_url: null,
                    label: { en: "New link", ar: "" },
                    subtitle: { en: "", ar: "" },
                    url: "",
                    enabled: true,
                    featured: false,
                    show_arrow: true,
                    bg_color: null,
                    bg_gradient_end: null,
                    text_color: null,
                },
            ],
        }));
    };

    const patchButton = (bid, patch) => {
        setBusiness((b) => ({
            ...b,
            buttons: b.buttons.map((btn) => (btn.id === bid ? { ...btn, ...patch } : btn)),
        }));
    };

    const removeButton = (bid) => {
        setBusiness((b) => ({ ...b, buttons: b.buttons.filter((btn) => btn.id !== bid) }));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const from = result.source.index;
        const to = result.destination.index;
        setBusiness((b) => {
            const arr = [...b.buttons];
            const [moved] = arr.splice(from, 1);
            arr.splice(to, 0, moved);
            return { ...b, buttons: arr };
        });
    };

    const applyTemplate = (tid) => {
        setPickedTemplate(tid);
        if (!tid) return;
        const src = allBusinesses.find((x) => x.id === tid);
        if (!src) return;
        const seeded = stripTemplate(src);
        setBusiness(seeded);
        // Do NOT update originalSnapshot — user should still save
        toast.success(`Template applied. Edit and save to create.`);
    };

    const validate = () => {
        if (!business.name?.en?.trim()) {
            toast.error("English business name is required");
            return false;
        }
        if (!business.slug?.trim()) {
            toast.error("URL slug is required");
            return false;
        }
        return true;
    };

    const save = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            // Normalize: empty strings in per-button colors -> null so backend
            // treats them as "use default".
            const cleanButtons = (business.buttons || []).map((b) => ({
                ...b,
                bg_color: b.bg_color && b.bg_color.trim() ? b.bg_color : null,
                bg_gradient_end: b.bg_gradient_end && b.bg_gradient_end.trim() ? b.bg_gradient_end : null,
                text_color: b.text_color && b.text_color.trim() ? b.text_color : null,
                icon_url: b.icon_url || null,
            }));
            const payload = { ...business, slug: slugifyClient(business.slug), buttons: cleanButtons };
            if (isNew) {
                const { data } = await api.post("/businesses", payload);
                toast.success("Business created");
                setOriginalSnapshot(JSON.stringify(data));
                navigate(`/admin/edit/${data.id}`, { replace: true });
            } else {
                const { data } = await api.put(`/businesses/${id}`, payload);
                const merged = { ...emptyBusiness(), ...data };
                setBusiness(merged);
                setOriginalSnapshot(JSON.stringify(merged));
                toast.success("Changes saved");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const publicUrl = business.slug ? `${BACKEND_URL}/b/${business.slug}` : "";

    const copyUrl = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            toast.success("URL copied");
        } catch {
            toast.error("Could not copy URL");
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="h-6 w-6 rounded-full border-2 border-brand border-t-transparent animate-spin" />
            </div>
        );
    }

    const EditorPanel = (
        <div className="space-y-6">
            {isNew && allBusinesses.length > 0 && (
                <Section title="Start from template" number="" subtitle="Optional. Duplicate an existing business, then edit only what changes.">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <Select value={pickedTemplate} onValueChange={applyTemplate}>
                            <SelectTrigger className="w-full sm:w-80 bg-zinc-900 border-white/10 text-zinc-100" data-testid="template-select">
                                <SelectValue placeholder="Choose a business to copy from…" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 text-zinc-100 border-white/10 max-h-72">
                                {allBusinesses.map((b) => (
                                    <SelectItem key={b.id} value={b.id} data-testid={`template-option-${b.slug}`}>
                                        {b.name?.en || b.slug}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {pickedTemplate && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setPickedTemplate("");
                                    setBusiness(emptyBusiness());
                                }}
                                className="text-zinc-400 hover:text-zinc-100 hover:bg-white/5"
                                data-testid="template-clear"
                            >
                                Start blank
                            </Button>
                        )}
                    </div>
                </Section>
            )}

            <Section title="Basics" number="01">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Business name · English *">
                        <Input
                            value={business.name?.en || ""}
                            onChange={(e) => updateName("en", e.target.value)}
                            data-testid="edit-name-en"
                            placeholder="Cafe DinePulse"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Business name · Arabic">
                        <Input
                            value={business.name?.ar || ""}
                            onChange={(e) => updateName("ar", e.target.value)}
                            dir="rtl"
                            data-testid="edit-name-ar"
                            placeholder="مقهى داين بلس"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Welcome message · English">
                        <Textarea
                            rows={3}
                            value={business.welcome?.en || ""}
                            onChange={(e) => updateWelcome("en", e.target.value)}
                            data-testid="edit-welcome-en"
                            placeholder="Welcome! Tap a button below."
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Welcome message · Arabic">
                        <Textarea
                            rows={3}
                            value={business.welcome?.ar || ""}
                            onChange={(e) => updateWelcome("ar", e.target.value)}
                            dir="rtl"
                            data-testid="edit-welcome-ar"
                            placeholder="مرحباً! اضغط على أحد الأزرار أدناه."
                            className={inputCls}
                        />
                    </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Field label="URL slug *">
                        <div className="flex items-stretch">
                            <div className="px-3 flex items-center rounded-s-md border border-e-0 border-white/10 bg-white/5 text-zinc-400 text-xs font-mono">
                                /b/
                            </div>
                            <Input
                                value={business.slug || ""}
                                onChange={(e) => {
                                    setSlugTouched(true);
                                    update({ slug: slugifyClient(e.target.value) });
                                }}
                                data-testid="edit-slug"
                                placeholder="cafe-dinepulse"
                                className={`${inputCls} rounded-s-none font-mono`}
                            />
                        </div>
                        {publicUrl && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-zinc-500">
                                <span className="font-mono truncate">{publicUrl}</span>
                                <button type="button" onClick={copyUrl} className="text-brand hover:underline" data-testid="edit-copy-url">
                                    <Copy className="h-3 w-3 inline me-0.5" /> copy
                                </button>
                            </div>
                        )}
                    </Field>
                    <Field label="Default language">
                        <div className="inline-flex rounded-md border border-white/10 p-0.5 h-10 bg-zinc-900">
                            {["en", "ar"].map((l) => (
                                <button
                                    key={l}
                                    type="button"
                                    onClick={() => update({ default_lang: l })}
                                    className={`px-4 h-full text-sm rounded transition-colors ${
                                        business.default_lang === l ? "bg-brand text-white" : "text-zinc-400"
                                    }`}
                                    data-testid={`edit-default-lang-${l}`}
                                >
                                    {l === "en" ? "English" : "العربية"}
                                </button>
                            ))}
                        </div>
                    </Field>
                </div>

                <div className="flex items-center justify-between mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
                    <div>
                        <div className="text-sm font-semibold text-zinc-100">
                            Page is {business.is_active ? "active" : "inactive"}
                        </div>
                        <div className="text-xs text-zinc-400">
                            Inactive pages return a "not available" screen.
                        </div>
                    </div>
                    <Switch
                        checked={!!business.is_active}
                        onCheckedChange={(v) => update({ is_active: v })}
                        data-testid="edit-toggle-active"
                    />
                </div>
            </Section>

            <Section title="Hero & content" number="02" subtitle="Top image and text customers see first.">
                <div>
                    <ImageUpload
                        label="Full-page background image"
                        value={business.background_url}
                        onChange={(v) => update({ background_url: v })}
                        testId="edit-background-upload"
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Field label="Hero title · English">
                        <Input
                            value={business.hero_title?.en || ""}
                            onChange={(e) => updateLocalized("hero_title", "en", e.target.value)}
                            placeholder="Thank you for visiting us! 🙏"
                            data-testid="edit-hero-title-en"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Hero title · Arabic">
                        <Input
                            value={business.hero_title?.ar || ""}
                            onChange={(e) => updateLocalized("hero_title", "ar", e.target.value)}
                            placeholder="شكراً لزيارتنا"
                            dir="rtl"
                            data-testid="edit-hero-title-ar"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Hero subtitle · English">
                        <Input
                            value={business.hero_subtitle?.en || ""}
                            onChange={(e) => updateLocalized("hero_subtitle", "en", e.target.value)}
                            placeholder="We'd love to stay connected with you"
                            data-testid="edit-hero-subtitle-en"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Hero subtitle · Arabic">
                        <Input
                            value={business.hero_subtitle?.ar || ""}
                            onChange={(e) => updateLocalized("hero_subtitle", "ar", e.target.value)}
                            placeholder="نتمنى أن نبقى على تواصل"
                            dir="rtl"
                            data-testid="edit-hero-subtitle-ar"
                            className={inputCls}
                        />
                    </Field>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <Field label="Footer text · English">
                        <Input
                            value={business.footer_text?.en || ""}
                            onChange={(e) => updateLocalized("footer_text", "en", e.target.value)}
                            placeholder="We appreciate your support!"
                            data-testid="edit-footer-en"
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Footer text · Arabic">
                        <Input
                            value={business.footer_text?.ar || ""}
                            onChange={(e) => updateLocalized("footer_text", "ar", e.target.value)}
                            placeholder="شكراً لدعمكم!"
                            dir="rtl"
                            data-testid="edit-footer-ar"
                            className={inputCls}
                        />
                    </Field>
                </div>

                <div className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between mb-3">
                        <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400">
                            Dark overlay on background
                        </Label>
                        <span className="text-xs font-mono text-zinc-300">
                            {Math.round((business.overlay_opacity ?? 0.35) * 100)}%
                        </span>
                    </div>
                    <Slider
                        min={0}
                        max={100}
                        step={1}
                        value={[Math.round((business.overlay_opacity ?? 0.35) * 100)]}
                        onValueChange={(v) => update({ overlay_opacity: (v?.[0] ?? 35) / 100 })}
                        data-testid="edit-overlay-opacity"
                    />
                    <div className="mt-2 text-xs text-zinc-500">
                        Increase the overlay to improve readability over bright background images.
                    </div>
                </div>
            </Section>

            <Section title="Colors" number="03">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <ColorField label="Background (fallback)" value={business.background_color} onChange={(v) => update({ background_color: v })} testId="color-bg" />
                    <ColorField label="Card background" value={business.card_bg_color} onChange={(v) => update({ card_bg_color: v })} testId="color-card-bg" />
                    <ColorField label="Card text" value={business.card_text_color} onChange={(v) => update({ card_text_color: v })} testId="color-card-text" />
                    <ColorField label="Default button bg" value={business.button_bg_color} onChange={(v) => update({ button_bg_color: v })} testId="color-btn-bg" />
                    <ColorField label="Default button text" value={business.button_text_color} onChange={(v) => update({ button_text_color: v })} testId="color-btn-text" />
                    <ColorField label="Default featured bg" value={business.featured_bg_color} onChange={(v) => update({ featured_bg_color: v })} testId="color-feat-bg" />
                    <ColorField label="Default featured text" value={business.featured_text_color} onChange={(v) => update({ featured_text_color: v })} testId="color-feat-text" />
                </div>
            </Section>

            <Section
                title="Buttons"
                number="04"
                action={
                    <Button
                        type="button"
                        onClick={addButton}
                        variant="outline"
                        size="sm"
                        data-testid="add-button"
                        className="border-brand text-brand hover:bg-brand/10 bg-transparent"
                    >
                        <Plus className="h-4 w-4 me-1" />
                        Add button
                    </Button>
                }
            >
                {(business.buttons || []).length === 0 ? (
                    <div className="border border-dashed border-white/10 rounded-lg p-8 text-center bg-white/5">
                        <div className="text-sm text-zinc-400">No buttons yet. Add your first link.</div>
                        <Button type="button" onClick={addButton} className="mt-4 bg-brand hover:bg-brand-hover text-white" size="sm" data-testid="empty-add-button">
                            <Plus className="h-4 w-4 me-1" />
                            Add button
                        </Button>
                    </div>
                ) : (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="buttons">
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className="space-y-3"
                                    data-testid="button-list"
                                >
                                    {business.buttons.map((btn, index) => (
                                        <Draggable key={btn.id} draggableId={btn.id} index={index}>
                                            {(dragProvided, snapshot) => (
                                                <div
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    className={`bg-zinc-900 border border-white/10 rounded-lg p-3 ${
                                                        snapshot.isDragging ? "shadow-lg scale-[1.01] border-brand/50" : ""
                                                    }`}
                                                    data-testid={`button-row-${index}`}
                                                >
                                                    <div className="flex items-start gap-2">
                                                        <button
                                                            type="button"
                                                            {...dragProvided.dragHandleProps}
                                                            className="p-2 text-zinc-500 hover:text-zinc-200 cursor-grab active:cursor-grabbing shrink-0"
                                                            aria-label="Drag to reorder"
                                                        >
                                                            <GripVertical className="h-4 w-4" />
                                                        </button>
                                                        <IconPicker
                                                            value={btn.icon}
                                                            onChange={(v) => patchButton(btn.id, { icon: v })}
                                                            testId={`button-icon-${index}`}
                                                        />
                                                        <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                            <Input
                                                                value={btn.subtitle?.en || ""}
                                                                onChange={(e) => patchButton(btn.id, { subtitle: { ...btn.subtitle, en: e.target.value } })}
                                                                placeholder="Subtitle EN — e.g. 'Follow us on'"
                                                                data-testid={`button-subtitle-en-${index}`}
                                                                className={inputCls}
                                                            />
                                                            <Input
                                                                value={btn.subtitle?.ar || ""}
                                                                onChange={(e) => patchButton(btn.id, { subtitle: { ...btn.subtitle, ar: e.target.value } })}
                                                                placeholder="العنوان الفرعي (AR)"
                                                                dir="rtl"
                                                                data-testid={`button-subtitle-ar-${index}`}
                                                                className={inputCls}
                                                            />
                                                            <Input
                                                                value={btn.label?.en || ""}
                                                                onChange={(e) => patchButton(btn.id, { label: { ...btn.label, en: e.target.value } })}
                                                                placeholder="Title EN — e.g. 'Instagram'"
                                                                data-testid={`button-label-en-${index}`}
                                                                className={`${inputCls} font-semibold`}
                                                            />
                                                            <Input
                                                                value={btn.label?.ar || ""}
                                                                onChange={(e) => patchButton(btn.id, { label: { ...btn.label, ar: e.target.value } })}
                                                                placeholder="التسمية (AR)"
                                                                dir="rtl"
                                                                data-testid={`button-label-ar-${index}`}
                                                                className={`${inputCls} font-semibold`}
                                                            />
                                                            <Input
                                                                value={btn.url || ""}
                                                                onChange={(e) => patchButton(btn.id, { url: e.target.value })}
                                                                placeholder="https://…  or  tel:  mailto:  whatsapp://"
                                                                className={`${inputCls} sm:col-span-2 font-mono text-xs`}
                                                                data-testid={`button-url-${index}`}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="mt-2 flex flex-wrap items-center gap-4 ps-8 text-xs text-zinc-400">
                                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                                            <Switch
                                                                checked={btn.enabled !== false}
                                                                onCheckedChange={(v) => patchButton(btn.id, { enabled: v })}
                                                                data-testid={`button-enabled-${index}`}
                                                            />
                                                            Enabled
                                                        </label>
                                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                                            <Switch
                                                                checked={!!btn.featured}
                                                                onCheckedChange={(v) => patchButton(btn.id, { featured: v })}
                                                                data-testid={`button-featured-${index}`}
                                                            />
                                                            <Star className={`h-3.5 w-3.5 ${btn.featured ? "text-brand fill-brand" : ""}`} />
                                                            Featured
                                                        </label>
                                                        <label className="inline-flex items-center gap-2 cursor-pointer">
                                                            <Switch
                                                                checked={btn.show_arrow !== false}
                                                                onCheckedChange={(v) => patchButton(btn.id, { show_arrow: v })}
                                                                data-testid={`button-arrow-${index}`}
                                                            />
                                                            <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                                                            Show arrow
                                                        </label>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeButton(btn.id)}
                                                            className="ms-auto text-red-400 hover:text-red-300 inline-flex items-center gap-1"
                                                            data-testid={`button-remove-${index}`}
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                            Remove
                                                        </button>
                                                    </div>

                                                    <Collapsible>
                                                        <CollapsibleTrigger asChild>
                                                            <button
                                                                type="button"
                                                                className="mt-3 ms-8 inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-100"
                                                                data-testid={`button-colors-toggle-${index}`}
                                                            >
                                                                <Palette className="h-3.5 w-3.5" />
                                                                Style · colors · custom icon
                                                                <ChevronDown className="h-3 w-3" />
                                                            </button>
                                                        </CollapsibleTrigger>
                                                        <CollapsibleContent>
                                                            <div className="mt-3 ms-8 space-y-3">
                                                                <div className="p-3 rounded-md bg-black/30 border border-white/5">
                                                                    <ImageUpload
                                                                        label="Custom icon (overrides icon selection)"
                                                                        value={btn.icon_url}
                                                                        onChange={(v) => patchButton(btn.id, { icon_url: v })}
                                                                        testId={`button-icon-upload-${index}`}
                                                                    />
                                                                </div>
                                                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                                    <ColorField
                                                                        label="Button background"
                                                                        value={btn.bg_color || ""}
                                                                        onChange={(v) => patchButton(btn.id, { bg_color: v || null })}
                                                                        testId={`button-bg-color-${index}`}
                                                                        allowEmpty
                                                                        fallbackHint={btn.featured ? business.featured_bg_color : business.button_bg_color}
                                                                    />
                                                                    <ColorField
                                                                        label="Gradient end (optional)"
                                                                        value={btn.bg_gradient_end || ""}
                                                                        onChange={(v) => patchButton(btn.id, { bg_gradient_end: v || null })}
                                                                        testId={`button-gradient-end-${index}`}
                                                                        allowEmpty
                                                                        fallbackHint="no gradient"
                                                                    />
                                                                    <ColorField
                                                                        label="Button text"
                                                                        value={btn.text_color || ""}
                                                                        onChange={(v) => patchButton(btn.id, { text_color: v || null })}
                                                                        testId={`button-text-color-${index}`}
                                                                        allowEmpty
                                                                        fallbackHint={btn.featured ? business.featured_text_color : business.button_text_color}
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => patchButton(btn.id, { bg_color: null, bg_gradient_end: null, text_color: null, icon_url: null })}
                                                                        className="text-xs text-zinc-400 hover:text-zinc-200 underline"
                                                                        data-testid={`button-colors-reset-${index}`}
                                                                    >
                                                                        Reset style to defaults
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </CollapsibleContent>
                                                    </Collapsible>
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}
            </Section>
        </div>
    );

    return (
        <div className="min-h-full">
            {/* Header (sticky) */}
            <div className="sticky top-14 md:top-0 z-20 bg-zinc-950/85 backdrop-blur-md border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-3 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/admin")}
                        className="text-zinc-400 hover:text-zinc-100"
                        data-testid="editor-back"
                        aria-label="Back"
                    >
                        <ArrowLeft className="h-5 w-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                            {isNew ? "New business" : "Editing"}
                            {dirty && (
                                <span className="inline-flex items-center gap-1 text-amber-400" data-testid="editor-dirty-indicator">
                                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                                    unsaved
                                </span>
                            )}
                        </div>
                        <div className="font-display font-semibold truncate text-zinc-100">
                            {business.name?.en || (isNew ? "Untitled" : "Business")}
                        </div>
                    </div>
                    {publicUrl && !isNew && (
                        <a href={`/b/${business.slug}`} target="_blank" rel="noreferrer" className="hidden md:inline-flex">
                            <Button variant="outline" size="sm" data-testid="editor-preview-link"
                                className="border-white/10 text-zinc-200 bg-transparent hover:bg-white/5">
                                <Eye className="h-3.5 w-3.5 me-1.5" />
                                Open page
                            </Button>
                        </a>
                    )}
                    <Button
                        onClick={save}
                        disabled={saving || !dirty}
                        className="bg-brand hover:bg-brand-hover text-white disabled:opacity-40"
                        data-testid="editor-save-button"
                    >
                        <Save className="h-4 w-4 me-1.5" />
                        {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
                    </Button>
                </div>
            </div>

            {/* Body */}
            <div className="max-w-[1400px] mx-auto px-4 md:px-10 py-6 md:py-10">
                {isDesktop ? (
                    <div className="grid grid-cols-3 gap-10 items-start">
                        <div className="col-span-2">{EditorPanel}</div>
                        <div className="sticky top-24">
                            <LivePreview business={business} />
                        </div>
                    </div>
                ) : (
                    <Tabs value={mobileTab} onValueChange={setMobileTab}>
                        <TabsList className="grid w-full grid-cols-2 mb-6 bg-zinc-900 border border-white/10">
                            <TabsTrigger value="edit" data-testid="mobile-tab-edit" className="data-[state=active]:bg-brand data-[state=active]:text-white">
                                <PencilLine className="h-4 w-4 me-1.5" /> Edit
                            </TabsTrigger>
                            <TabsTrigger value="preview" data-testid="mobile-tab-preview" className="data-[state=active]:bg-brand data-[state=active]:text-white">
                                <Smartphone className="h-4 w-4 me-1.5" /> Preview
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="edit">{EditorPanel}</TabsContent>
                        <TabsContent value="preview">
                            <div className="flex justify-center">
                                <LivePreview business={business} />
                            </div>
                        </TabsContent>
                    </Tabs>
                )}

                {/* Floating save on mobile */}
                {!isDesktop && dirty && (
                    <div className="fixed bottom-4 inset-x-4 z-20">
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="w-full h-12 bg-brand hover:bg-brand-hover text-white shadow-[0_10px_30px_-10px_rgba(255,90,31,0.6)]"
                            data-testid="editor-save-button-mobile"
                        >
                            <Save className="h-4 w-4 me-2" />
                            {saving ? "Saving…" : "Save changes"}
                        </Button>
                    </div>
                )}
            </div>

            {/* Navigation blocker dialog */}
            <AlertDialog
                open={blocker.state === "blocked"}
                onOpenChange={(open) => {
                    if (!open && blocker.state === "blocked") blocker.reset?.();
                }}
            >
                <AlertDialogContent className="bg-zinc-900 border-white/10 text-zinc-100">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-400" />
                            Unsaved changes
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-zinc-400">
                            You have unsaved changes on this page. Leaving now will discard them.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            className="bg-transparent border-white/10 text-zinc-200 hover:bg-white/5"
                            data-testid="unsaved-cancel"
                        >
                            Stay on page
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => blocker.proceed?.()}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            data-testid="unsaved-leave"
                        >
                            Discard & leave
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------
const inputCls = "bg-zinc-900 border-white/10 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-brand";

function Section({ title, number, action, children, subtitle }) {
    return (
        <section className="bg-zinc-900/70 border border-white/10 rounded-xl p-5 md:p-6">
            <div className="flex items-start justify-between mb-5 gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    {number && (
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-brand pt-1.5">{number}</span>
                    )}
                    <div className="min-w-0">
                        <h2 className="font-display text-lg font-semibold tracking-tight text-zinc-100">{title}</h2>
                        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
                    </div>
                </div>
                {action}
            </div>
            {children}
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}

function ColorField({ label, value, onChange, testId, allowEmpty = false, fallbackHint }) {
    const isEmpty = allowEmpty && !value;
    // When value is empty and allowEmpty=true, the native color input should NOT
    // be pre-set to fallbackHint (that caused React re-renders to overwrite the
    // reset with the fallback value). Use a neutral gray placeholder instead;
    // only fire onChange when user actively picks a color.
    const pickerValue = value || (allowEmpty ? "#808080" : fallbackHint || "#000000");
    return (
        <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</Label>
            <div className="flex items-stretch gap-2">
                <input
                    type="color"
                    value={pickerValue}
                    onChange={(e) => {
                        const v = e.target.value;
                        if (allowEmpty && !value && v.toLowerCase() === "#808080") return;
                        onChange(v);
                    }}
                    className={`h-10 w-12 rounded-md border border-white/10 cursor-pointer bg-zinc-900 ${
                        isEmpty ? "opacity-60" : ""
                    }`}
                    data-testid={`${testId}-picker`}
                    aria-label={label}
                />
                <Input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className={`${inputCls} font-mono uppercase`}
                    placeholder={allowEmpty && fallbackHint ? `default ${fallbackHint}` : ""}
                    data-testid={`${testId}-input`}
                />
            </div>
            {isEmpty && fallbackHint && (
                <div className="text-[11px] text-zinc-500">Using default: <span className="font-mono">{fallbackHint}</span></div>
            )}
        </div>
    );
}
